/**
 * AdminTranslations — the translation round trip.
 *
 * Export every translatable string for a language as one .xlsx, send it to a
 * translator, import the answers back. Repeatable: a re-export always carries
 * the current state, so the same file can go round as many times as it takes.
 *
 * The file covers BOTH halves — the UI dictionary strings from the code and
 * every piece of admin-authored database content — because a translator should
 * see one job, not two systems.
 *
 * Import is preview-then-commit: a dry run reports what would change and what
 * it could not place, and nothing is written until that is confirmed. An import
 * touches every string in the product at once, so "run it and see" is not an
 * acceptable interaction.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Languages, Download, Upload, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  fetchLexicon,
  fetchLexiconCoverage,
  submitLexicon,
  type LexiconCoverage,
  type LexiconImportResult,
} from '../../services/lexiconApi';
import { downloadLexicon, parseLexiconFile } from '../../services/lexiconSheet';
import { fetchTranslations, type TranslatableLocale } from '../../services/translationsApi';

export const AdminTranslations: React.FC = () => {
  const [locales, setLocales] = useState<TranslatableLocale[]>([]);
  const [locale, setLocale] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'export' | 'preview' | 'commit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Per-language coverage, and which language's examples are expanded.
  const [coverage, setCoverage] = useState<LexiconCoverage[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // The parsed file waiting on confirmation, plus its dry-run result.
  const [pending, setPending] = useState<{ ref: string; value: string }[] | null>(null);
  const [pendingName, setPendingName] = useState('');
  const [preview, setPreview] = useState<LexiconImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // The locale list comes from the translations endpoint, which already returns
  // exactly the right set: active, and never English.
  useEffect(() => {
    let cancelled = false;
    fetchTranslations('ui_string', [])
      .then(res => {
        if (cancelled) return;
        setLocales(res.locales);
        setLocale(prev => prev || res.locales[0]?.code || '');
      })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load locales'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Coverage is a second, heavier call (it builds the lexicon per language),
    // so it loads independently rather than holding up the page.
    fetchLexiconCoverage()
      .then(c => { if (!cancelled) setCoverage(c); })
      .catch(() => { if (!cancelled) setCoverage([]); });
    return () => { cancelled = true; };
  }, []);

  const refreshCoverage = useCallback(() => {
    fetchLexiconCoverage().then(setCoverage).catch(() => {});
  }, []);

  const clearPending = useCallback(() => {
    setPending(null);
    setPreview(null);
    setPendingName('');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleExport = async () => {
    if (!locale) return;
    setBusy('export');
    setError(null);
    setMessage(null);
    try {
      const data = await fetchLexicon(locale);
      downloadLexicon(data.rows, locale);
      setMessage(
        `Exported ${data.counts.total} strings for ${locale} — ` +
        `${data.counts.ui} interface, ${data.counts.db} content, ` +
        `${data.counts.translated} already translated.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  const handleFile = async (file: File) => {
    setError(null);
    setMessage(null);
    setBusy('preview');
    try {
      const parsed = await parseLexiconFile(file);
      if (parsed.missingHeaders.length) {
        setError(
          `That file is missing the ${parsed.missingHeaders.join(' and ')} column` +
          `${parsed.missingHeaders.length > 1 ? 's' : ''}. Export a fresh copy and translate the TRANSLATION column.`,
        );
        clearPending();
        return;
      }
      if (parsed.rows.length === 0) {
        setError('No rows found in that file.');
        clearPending();
        return;
      }
      const dry = await submitLexicon(locale, parsed.rows, true);
      setPending(parsed.rows);
      setPendingName(file.name);
      setPreview(dry);
      if (parsed.malformed > 0) {
        setMessage(`${parsed.malformed} row${parsed.malformed === 1 ? '' : 's'} had no REFERENCE and will be ignored.`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file');
      clearPending();
    } finally {
      setBusy(null);
    }
  };

  const handleCommit = async () => {
    if (!pending) return;
    setBusy('commit');
    setError(null);
    try {
      const res = await submitLexicon(locale, pending, false);
      setMessage(
        `Imported ${res.applied} translation${res.applied === 1 ? '' : 's'}` +
        (res.cleared ? `, cleared ${res.cleared}` : '') +
        `. Reload the estimator to see them.`,
      );
      clearPending();
      refreshCoverage();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="text-muted small d-flex align-items-center gap-2 py-4">
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
      </div>
    );
  }

  return (
    <div>
      <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
        <Languages size={20} /> Translations
      </h2>
      <p className="text-muted small mb-4">
        Export every translatable string as a spreadsheet, send it for translation, and import
        the answers back. Covers both the interface text and your own content — products, buttons,
        menus, categories and locations — in one file.
      </p>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}
      {message && (
        <div className="alert alert-success py-2 small d-flex align-items-start gap-2">
          <CheckCircle2 size={16} className="flex-shrink-0 mt-1" /> <span>{message}</span>
        </div>
      )}

      {locales.length === 0 ? (
        <div className="alert alert-warning py-2 small mb-0">
          No languages besides English are active yet. Add one before exporting.
        </div>
      ) : (
        <>
          {coverage && coverage.length > 0 && (
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h6 className="fw-bold mb-1">Coverage</h6>
                <p className="text-muted small mb-3">
                  Anything untranslated falls back to English silently — nothing on the site says
                  a string is missing, so this is where to check.
                </p>
                {coverage.map(c => {
                  const pct = c.total > 0 ? Math.round((c.translated / c.total) * 100) : 100;
                  const complete = c.missing === 0;
                  return (
                    <div key={c.code} className="mb-3">
                      <div className="d-flex align-items-center justify-content-between small mb-1">
                        <span>
                          <strong>{c.englishName}</strong>
                          <code className="ms-2 text-muted">{c.code}</code>
                        </span>
                        <span className={complete ? 'text-success' : 'text-warning-emphasis'}>
                          {complete ? (
                            <><CheckCircle2 size={14} className="me-1" />complete</>
                          ) : (
                            <>{c.missing} missing of {c.total}</>
                          )}
                        </span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className={`progress-bar ${complete ? 'bg-success' : 'bg-warning'}`}
                          style={{ width: `${pct}%` }}
                          role="progressbar"
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      {!complete && (
                        <>
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 mt-1"
                            style={{ fontSize: 12 }}
                            onClick={() => setExpanded(expanded === c.code ? null : c.code)}
                          >
                            {expanded === c.code ? 'Hide' : 'Show'} examples
                          </button>
                          {expanded === c.code && (
                            <ul className="list-unstyled small text-muted mt-1 mb-0 ps-2">
                              {c.missingSample.map(m => (
                                <li key={m.ref} className="text-truncate">
                                  <span className="text-body">{m.english}</span>
                                  <span className="ms-2" style={{ fontSize: 11 }}>{m.context}</span>
                                </li>
                              ))}
                              {c.missing > c.missingSample.length && (
                                <li style={{ fontSize: 11 }}>
                                  …and {c.missing - c.missingSample.length} more — export to see them all
                                </li>
                              )}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <label className="form-label small fw-semibold">Language</label>
              <select
                className="form-select form-select-sm mb-3"
                style={{ maxWidth: 320 }}
                value={locale}
                disabled={busy !== null || pending !== null}
                onChange={e => { setLocale(e.target.value); clearPending(); }}
              >
                {locales.map(l => (
                  <option key={l.code} value={l.code}>
                    {l.englishName} — {l.nativeName} ({l.code})
                  </option>
                ))}
              </select>

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                  onClick={handleExport}
                  disabled={busy !== null}
                >
                  {busy === 'export'
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Download size={14} />}
                  Export lexicon (.xlsx)
                </button>

                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy !== null}
                >
                  <Upload size={14} /> Import translated file…
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="d-none"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
              </div>

              <p className="text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
                Translators fill in the <strong>TRANSLATION</strong> column and leave the rest
                alone. A blank translation falls back to English. Re-exporting always includes
                anything already imported, so the same file can go round more than once.
              </p>
            </div>
          </div>

          {preview && pending && (
            <div className="card shadow-sm border-primary">
              <div className="card-body">
                <h5 className="fw-bold mb-1">Ready to import</h5>
                <p className="text-muted small mb-3">
                  <code>{pendingName}</code> — nothing has been saved yet.
                </p>

                <ul className="list-unstyled small mb-3">
                  <li><strong>{preview.applied}</strong> translation{preview.applied === 1 ? '' : 's'} to add or update</li>
                  {preview.cleared > 0 && (
                    <li><strong>{preview.cleared}</strong> to clear (reverting to English)</li>
                  )}
                  <li className="text-muted">{preview.unchanged} already up to date</li>
                  {preview.remapped > 0 && (
                    <li className="text-muted">
                      {preview.remapped} matched by English text rather than reference
                      (the file was exported from another environment, or content was renumbered)
                    </li>
                  )}
                </ul>

                {preview.skipped.length > 0 && (
                  <div className="alert alert-warning py-2 small">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <AlertTriangle size={14} />
                      <strong>{preview.skipped.length} row{preview.skipped.length === 1 ? '' : 's'} could not be placed</strong>
                    </div>
                    {/* Showing these matters: the usual cause is a translator
                        editing the REFERENCE column, and it is silent otherwise. */}
                    <ul className="mb-0 ps-3">
                      {preview.skipped.slice(0, 8).map((s, i) => (
                        <li key={i}><code>{s.ref || '(blank)'}</code> — {s.reason}</li>
                      ))}
                      {preview.skipped.length > 8 && (
                        <li className="text-muted">…and {preview.skipped.length - 8} more</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2"
                    onClick={handleCommit}
                    disabled={busy !== null || preview.applied + preview.cleared === 0}
                  >
                    {busy === 'commit'
                      ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <CheckCircle2 size={14} />}
                    Apply {preview.applied + preview.cleared} change
                    {preview.applied + preview.cleared === 1 ? '' : 's'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={clearPending}
                    disabled={busy !== null}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminTranslations;
